/**
 * Sentry Error Tracking Utilities
 * Centralized error handling and reporting
 */

import * as Sentry from '@sentry/nextjs'

/**
 * Capture an exception and send it to Sentry
 */
export function captureException(error: Error | unknown, context?: Record<string, any>) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value)
        })
      }
      Sentry.captureException(error)
    })
  }

  // Always log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Captured exception:', error, context)
  }
}

/**
 * Capture a message and send it to Sentry
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value)
        })
      }
      Sentry.captureMessage(message, level)
    })
  }

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    const logFn = level === 'error' ? console.error : level === 'warning' ? console.warn : console.log
    logFn('Captured message:', message, context)
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.addBreadcrumb(breadcrumb)
  }
}

/**
 * Set user context for error tracking
 */
export function setUser(user: Sentry.User | null) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.setUser(user)
  }
}

/**
 * Set custom tags
 */
export function setTag(key: string, value: string) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.setTag(key, value)
  }
}

/**
 * Set custom context
 */
export function setContext(name: string, context: Record<string, any>) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.setContext(name, context)
  }
}

/**
 * Wrap async functions with error tracking
 */
export function withErrorTracking<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options?: {
    name?: string
    context?: Record<string, any>
  }
): T {
  return (async (...args: any[]) => {
    try {
      return await fn(...args)
    } catch (error) {
      captureException(error, {
        functionName: options?.name || fn.name,
        ...options?.context,
      })
      throw error
    }
  }) as T
}

/**
 * Wrap sync functions with error tracking
 */
export function withSyncErrorTracking<T extends (...args: any[]) => any>(
  fn: T,
  options?: {
    name?: string
    context?: Record<string, any>
  }
): T {
  return ((...args: any[]) => {
    try {
      return fn(...args)
    } catch (error) {
      captureException(error, {
        functionName: options?.name || fn.name,
        ...options?.context,
      })
      throw error
    }
  }) as T
}

/**
 * Report handled errors (non-critical errors that were caught and handled)
 */
export function reportHandledError(error: Error | unknown, context?: Record<string, any>) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      scope.setLevel('warning')
      scope.setTag('handled', 'true')

      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value)
        })
      }

      Sentry.captureException(error)
    })
  }

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.warn('Handled error:', error, context)
  }
}

/**
 * Track performance
 * Note: startTransaction is deprecated in newer Sentry versions
 * Use Sentry.startSpan instead if needed
 */
export function startTransaction(name: string, op: string) {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    // startTransaction has been removed in favor of startSpan
    // Return null for now - this function is kept for backwards compatibility
    return null
  }
  return null
}

/**
 * Show user feedback dialog
 */
export function showFeedbackDialog() {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    const eventId = Sentry.lastEventId()
    if (eventId) {
      Sentry.showReportDialog({ eventId })
    }
  }
}
